import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "wouter";
import { Show, useUser, useClerk, useAuth } from "@clerk/react";
import { ShoppingCart, Menu, X, ChevronDown, User, LogOut } from "lucide-react";
import { useGetCart, useGetMyProfile, getGetCartQueryKey, getGetMyProfileQueryKey } from "@workspace/api-client-react";

const TIER_DIAMOND: Record<string, { color: string; label: string }> = {
  basic:     { color: "#9CA3AF", label: "Basic" },
  essentials:{ color: "#FACC15", label: "Essentials" },
  growth:    { color: "#FF6B00", label: "Growth" },
  authority: { color: "#A855F7", label: "Authority" },
};

const INDUSTRY_GROUPS = [
  {
    label: "Residential Services",
    industries: [
      { name: "Roofing", slug: "roofing" },
      { name: "HVAC", slug: "hvac" },
      { name: "Plumbing", slug: "plumbing" },
      { name: "Electrical", slug: "electrical" },
      { name: "Garage Doors", slug: "garage-doors" },
      { name: "Pest Control", slug: "pest-control" },
      { name: "Carpet Cleaning", slug: "carpet-cleaning" },
      { name: "Holiday Lighting", slug: "holiday-lighting" },
      { name: "Construction", slug: "construction" },
      { name: "General Contractors", slug: "general-contractors" },
    ],
  },
  {
    label: "Exterior Home Services",
    industries: [
      { name: "Landscaping", slug: "landscaping" },
      { name: "Lawn Care", slug: "lawn-care" },
      { name: "Tree Care", slug: "tree-care" },
      { name: "Exterior Cleaning", slug: "exterior-cleaning" },
      { name: "Power Washing", slug: "power-washing" },
      { name: "Window Cleaning & Installation", slug: "window-cleaning" },
      { name: "Decks", slug: "decks" },
      { name: "Fencing", slug: "fencing" },
      { name: "Garage Floor Coating", slug: "garage-floor-coating" },
      { name: "Painting", slug: "painting" },
    ],
  },
  {
    label: "Business & Consumer",
    industries: [
      { name: "Restaurants", slug: "restaurants" },
      { name: "Retail", slug: "retail" },
      { name: "E-Commerce", slug: "e-commerce" },
      { name: "Veterinary Services", slug: "veterinary-services" },
      { name: "Medical & Mental Health", slug: "medical-dental-mental-health" },
      { name: "Financial Services", slug: "financial-services" },
      { name: "Apartments", slug: "apartments" },
    ],
  },
  {
    label: "Commercial & Industrial",
    industries: [
      { name: "Farms & Agricultural Supply", slug: "farms" },
      { name: "Telecommunications", slug: "telecommunications" },
      { name: "Personal Protective Equipment", slug: "personal-protective-equipment" },
      { name: "Janitorial & Sanitation", slug: "janitorial-sanitation" },
      { name: "Industrial & Manufacturing", slug: "industrial-manufacturing-supplies" },
    ],
  },
  {
    label: "Community Organizations",
    industries: [
      { name: "Churches & Non-Profits", slug: "churches-nonprofits" },
    ],
  },
];

function CartIcon() {
  const { isSignedIn } = useUser();
  const { data: cart } = useGetCart({ query: { enabled: !!isSignedIn, queryKey: getGetCartQueryKey() } });
  const itemCount = cart?.itemCount || 0;
  return (
    <Link href={isSignedIn ? "/cart" : "/sign-in"} aria-label={`Shopping cart${itemCount ? `, ${itemCount} item${itemCount === 1 ? "" : "s"}` : ""}`} data-testid="link-cart" className="relative flex items-center justify-center w-10 h-10 rounded-full hover:bg-white/10 transition-colors">
      <ShoppingCart aria-hidden="true" className="w-5 h-5 text-foreground" />
      {itemCount > 0 && (
        <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
          {itemCount}
        </span>
      )}
    </Link>
  );
}

function UserMenu() {
  const { user, isSignedIn } = useUser();
  const { signOut } = useClerk();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");
  const { data: profile } = useGetMyProfile({ query: { enabled: !!isSignedIn, queryKey: getGetMyProfileQueryKey() } });
  const tier = profile?.subscriptionTier || "basic";
  const tierInfo = TIER_DIAMOND[tier] || TIER_DIAMOND.basic;

  const menuItems = [
    { label: "Order Status",    href: `/dashboard` },
    { label: "Quotes",          href: `/dashboard?tab=quotes` },
    { label: "Saved Designs",   href: `/dashboard?tab=saved-designs` },
    { label: "Mailing Lists",   href: `/dashboard?tab=mailing-list` },
    { label: "Settings",        href: `/dashboard?tab=settings` },
  ];

  useEffect(() => {
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  return (
    <div className="relative" ref={menuRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(!open)}
        aria-label="Account menu"
        aria-expanded={open}
        aria-controls="account-menu"
        aria-haspopup="menu"
        data-testid="button-user-menu"
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors text-foreground"
      >
          <svg aria-hidden="true" width="10" height="10" viewBox="0 0 12 12" fill={tierInfo.color} className="flex-shrink-0">
          <polygon points="6,0 12,6 6,12 0,6" />
        </svg>
        <User aria-hidden="true" className="w-4 h-4" />
        <div className="hidden sm:block text-left leading-tight">
          <div className="text-xs text-foreground/70">Hi, {user?.firstName || "there"}!</div>
          <div className="text-xs font-bold text-foreground flex items-center gap-1">
             Your Account <ChevronDown aria-hidden="true" className="w-3 h-3" />
          </div>
        </div>
        <ChevronDown aria-hidden="true" className="w-3 h-3 sm:hidden" />
      </button>
      {open && (
        <div id="account-menu" role="menu" className="absolute right-0 mt-2 w-52 bg-card border border-border rounded-lg shadow-xl z-50 overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-background/50">
              <svg aria-hidden="true" width="12" height="12" viewBox="0 0 12 12" fill={tierInfo.color} className="flex-shrink-0">
              <polygon points="6,0 12,6 6,12 0,6" />
            </svg>
            <span className="text-xs font-black uppercase tracking-wider" style={{ color: tierInfo.color }}>
              {tierInfo.label}
            </span>
          </div>
          {menuItems.map((item) => (
            <Link
              key={item.label}
              href={item.href}
              role="menuitem"
              onClick={() => setOpen(false)}
              className="flex items-center px-4 py-3 text-sm text-foreground hover:bg-white/5 transition-colors"
            >
              {item.label}
            </Link>
          ))}
          <hr className="border-border" />
          <button
            type="button"
            onClick={() => { signOut({ redirectUrl: basePath || "/" }); setOpen(false); }}
            data-testid="button-sign-out"
            role="menuitem"
            className="flex items-center gap-2 px-4 py-3 text-sm text-foreground hover:bg-white/5 transition-colors w-full text-left"
          >
            <LogOut aria-hidden="true" className="w-4 h-4" />
            Log Out
          </button>
        </div>
      )}
    </div>
  );
}

export default function Header() {
  const { isSignedIn } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [industriesOpen, setIndustriesOpen] = useState(false);
  const [platformOpen, setPlatformOpen] = useState(false);
  const aboutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const servicesTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const industriesTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const platformTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const headerRef = useRef<HTMLElement>(null);

  // Mobile accordion state
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileIndustriesOpen, setMobileIndustriesOpen] = useState(false);
  const [mobileOpenCategories, setMobileOpenCategories] = useState<Set<string>>(new Set());

  function toggleMobileCategory(label: string) {
    setMobileOpenCategories((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  }

  function openMenu(set: (v: boolean) => void, timer: React.MutableRefObject<ReturnType<typeof setTimeout> | null>) {
    if (timer.current) clearTimeout(timer.current);
    set(true);
  }
  function closeMenu(set: (v: boolean) => void, timer: React.MutableRefObject<ReturnType<typeof setTimeout> | null>) {
    timer.current = setTimeout(() => set(false), 120);
  }
  const [location] = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const closeMenus = () => {
      setAboutOpen(false);
      setServicesOpen(false);
      setIndustriesOpen(false);
      setPlatformOpen(false);
    };
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(event.target as Node)) closeMenus();
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeMenus();
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setAboutOpen(false);
    setServicesOpen(false);
    setIndustriesOpen(false);
    setPlatformOpen(false);
    setMobileServicesOpen(false);
    setMobileIndustriesOpen(false);
    setMobileOpenCategories(new Set());
  }, [location]);

  const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

  return (
    <header
      ref={headerRef}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#1A1A1A]/95 backdrop-blur-md shadow-lg shadow-black/30 border-b border-border" : "bg-[#1A1A1A]"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link href="/" data-testid="link-logo" className="flex items-center">
            <img src="/logo.webp" alt="Gorilla Works Marketing" width="232" height="58" className="h-[58px] w-auto" />
          </Link>

          {/* Desktop Nav */}
          <nav aria-label="Primary navigation" className="hidden lg:flex items-center gap-1">
            <Link href="/" data-testid="nav-home" className="px-3 py-2 text-sm font-medium text-foreground/80 hover:text-primary transition-colors rounded-md hover:bg-white/5">Home</Link>

            {/* About Dropdown */}
            <div className="relative" onMouseEnter={() => openMenu(setAboutOpen, aboutTimer)} onMouseLeave={() => closeMenu(setAboutOpen, aboutTimer)}>
              <button type="button" onClick={() => setAboutOpen(!aboutOpen)} aria-expanded={aboutOpen} aria-controls="about-menu" aria-haspopup="menu" className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-foreground/80 hover:text-primary transition-colors rounded-md hover:bg-white/5">
                About <ChevronDown aria-hidden="true" className="w-3 h-3" />
              </button>
              {aboutOpen && (
                <div id="about-menu" className="absolute top-full left-0 mt-1 w-40 bg-card border border-border rounded-lg shadow-xl z-50 overflow-hidden">
                  <Link href="/about" data-testid="nav-about" onClick={() => setAboutOpen(false)} className="block px-4 py-3 text-sm text-foreground/80 hover:text-primary hover:bg-white/5 transition-colors">About Us</Link>
                  <Link href="/contact" data-testid="nav-contact" onClick={() => setAboutOpen(false)} className="block px-4 py-3 text-sm text-foreground/80 hover:text-primary hover:bg-white/5 transition-colors">Contact</Link>
                  <a href="/blog" data-testid="nav-blog" className="block px-4 py-3 text-sm text-foreground/80 hover:text-primary hover:bg-white/5 transition-colors">Blog</a>
                  <Link href="/book-demo" onClick={() => setAboutOpen(false)} className="block px-4 py-3 text-sm font-bold text-primary hover:bg-white/5 transition-colors">Schedule Demo</Link>
                </div>
              )}
            </div>

            {/* Services Dropdown */}
            <div className="relative" onMouseEnter={() => openMenu(setServicesOpen, servicesTimer)} onMouseLeave={() => closeMenu(setServicesOpen, servicesTimer)}>
              <button type="button" onClick={() => setServicesOpen(!servicesOpen)} aria-expanded={servicesOpen} aria-controls="services-menu" aria-haspopup="menu" className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-foreground/80 hover:text-primary transition-colors rounded-md hover:bg-white/5">
                Services <ChevronDown aria-hidden="true" className="w-3 h-3" />
              </button>
              {servicesOpen && (
                <div id="services-menu" className="absolute top-full left-0 mt-1 w-56 bg-card border border-border rounded-lg shadow-xl z-50 overflow-hidden">
                  <Link href="/services/direct-mail" onClick={() => setServicesOpen(false)} className="block px-4 py-3 text-sm text-foreground/80 hover:text-primary hover:bg-white/5 transition-colors">
                    <div className="font-medium">Direct Mail</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Reach every mailbox in your target area</div>
                  </Link>
                  <Link href="/services/website-development" onClick={() => setServicesOpen(false)} className="block px-4 py-3 text-sm text-foreground/80 hover:text-primary hover:bg-white/5 transition-colors">
                    <div className="font-medium">Website Development</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Conversion-focused sites with analytics built in</div>
                  </Link>
                  <Link href="/services/seo" onClick={() => setServicesOpen(false)} className="block px-4 py-3 text-sm text-foreground/80 hover:text-primary hover:bg-white/5 transition-colors">
                    <div className="font-medium">SEO</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Rank higher and attribute the gains</div>
                  </Link>
                  <Link href="/services/google-meta-ads" onClick={() => setServicesOpen(false)} className="block px-4 py-3 text-sm text-foreground/80 hover:text-primary hover:bg-white/5 transition-colors">
                    <div className="font-medium">Google &amp; Meta Ads</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Paid campaigns with causal attribution</div>
                  </Link>
                </div>
              )}
            </div>

            {/* Industries Mega Menu trigger */}
            <div
              className="relative"
              onMouseEnter={() => openMenu(setIndustriesOpen, industriesTimer)}
              onMouseLeave={() => closeMenu(setIndustriesOpen, industriesTimer)}
            >
              <button type="button" onClick={() => setIndustriesOpen(!industriesOpen)} aria-expanded={industriesOpen} aria-controls="industries-menu" aria-haspopup="menu" className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-foreground/80 hover:text-primary transition-colors rounded-md hover:bg-white/5">
                Industries <ChevronDown aria-hidden="true" className="w-3 h-3" />
              </button>
            </div>

            {/* Platform Dropdown */}
            <div className="relative" onMouseEnter={() => openMenu(setPlatformOpen, platformTimer)} onMouseLeave={() => closeMenu(setPlatformOpen, platformTimer)}>
              <button type="button" onClick={() => setPlatformOpen(!platformOpen)} aria-expanded={platformOpen} aria-controls="platform-menu" aria-haspopup="menu" className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-foreground/80 hover:text-primary transition-colors rounded-md hover:bg-white/5">
                Platform <ChevronDown aria-hidden="true" className="w-3 h-3" />
              </button>
              {platformOpen && (
                <div id="platform-menu" className="absolute top-full left-0 mt-1 w-52 bg-card border border-border rounded-lg shadow-xl z-50 overflow-hidden">
                  <Link href="/custom-direct-mail" data-testid="nav-custom-direct-mail" onClick={() => setPlatformOpen(false)} className="block px-4 py-3 text-sm text-foreground/80 hover:text-primary hover:bg-white/5 transition-colors">
                    Custom Direct Mail
                  </Link>
                  <Link href="/integrations" data-testid="nav-integrations" onClick={() => setPlatformOpen(false)} className="block px-4 py-3 text-sm text-foreground/80 hover:text-primary hover:bg-white/5 transition-colors">
                    Integrations
                  </Link>
                  <Link href="/causal-analytics" data-testid="nav-causal-analytics" onClick={() => setPlatformOpen(false)} className="block px-4 py-3 text-sm text-foreground/80 hover:text-primary hover:bg-white/5 transition-colors">
                    Causal Analytics
                  </Link>
                  <Link href="/calculator" data-testid="nav-calculator" onClick={() => setPlatformOpen(false)} className="block px-4 py-3 text-sm text-foreground/80 hover:text-primary hover:bg-white/5 transition-colors">
                    Pricing Calculator
                  </Link>
                  <Link href="/map-intel" data-testid="nav-map-intel" onClick={() => setPlatformOpen(false)} className="block px-4 py-3 text-sm text-foreground/80 hover:text-primary hover:bg-white/5 transition-colors">
                    Map Intel
                  </Link>
                </div>
              )}
            </div>

            <Link href="/pricing" data-testid="nav-pricing" className="px-3 py-2 text-sm font-medium text-foreground/80 hover:text-primary transition-colors rounded-md hover:bg-white/5">Pricing</Link>
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-2">
            <CartIcon />
            {/* Dashboard — always visible; signed-out users authenticate first. */}
            <a
              href={isSignedIn ? "/app/" : "/sign-in?redirect_url=%2Fapp%2F"}
              data-testid="link-app-dashboard"
              className="hidden lg:inline-flex items-center px-4 py-2 text-sm font-bold text-[#1A1A1A] rounded-lg transition-colors"
              style={{ backgroundColor: "#FF6B00" }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = "#E05E00")}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = "#FF6B00")}
            >
              Dashboard
            </a>
            {/* Login — signed-out only. Use isSignedIn directly (not Clerk's
                <Show> component) so the button renders as soon as the hook
                resolves rather than waiting on Clerk's internal loading state. */}
            {!isSignedIn && (
              <Link
                href={`/sign-in?redirect_url=${encodeURIComponent(location + (typeof window !== "undefined" ? window.location.search : ""))}`}
                data-testid="link-login"
                className="hidden lg:inline-flex items-center px-4 py-2 text-sm font-medium text-foreground/80 hover:text-primary rounded-lg transition-colors hover:bg-white/5"
              >
                Login
              </Link>
            )}
            {isSignedIn && <UserMenu />}

            {/* Mobile menu button */}
            <button
              type="button"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={mobileOpen ? "Close navigation menu" : "Open navigation menu"}
              aria-expanded={mobileOpen}
              aria-controls="mobile-navigation"
              data-testid="button-mobile-menu"
              className="lg:hidden flex items-center justify-center w-10 h-10 rounded-lg hover:bg-white/10 transition-colors"
            >
              {mobileOpen ? <X aria-hidden="true" className="w-5 h-5" /> : <Menu aria-hidden="true" className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <nav id="mobile-navigation" aria-label="Mobile navigation" className="lg:hidden border-t border-border py-4 space-y-1 max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain pb-24">
            <Link href="/" className="block px-4 py-2 text-sm font-medium text-foreground/80 hover:text-primary">Home</Link>
            <Link href="/about" className="block px-4 py-2 text-sm font-medium text-foreground/80 hover:text-primary">About Us</Link>
            <Link href="/contact" className="block px-4 py-2 text-sm font-medium text-foreground/80 hover:text-primary">Contact</Link>
            <a href="/blog" data-testid="nav-blog-mobile" className="block px-4 py-2 text-sm font-medium text-foreground/80 hover:text-primary">Blog</a>

            {/* Mobile Services Accordion */}
            <div>
              <button
                type="button"
                className="flex items-center justify-between w-full px-4 py-2 text-sm font-medium text-foreground/80 hover:text-primary"
                onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                aria-expanded={mobileServicesOpen}
                aria-controls="mobile-services-menu"
              >
                <span>Services</span>
                <ChevronDown aria-hidden="true" className={`w-4 h-4 transition-transform duration-200 ${mobileServicesOpen ? "rotate-180" : ""}`} />
              </button>
              {mobileServicesOpen && (
                <div id="mobile-services-menu" className="pl-4">
                  <Link href="/services/direct-mail" className="block px-4 py-2 text-sm text-foreground/70 hover:text-primary">Direct Mail</Link>
                  <Link href="/services/website-development" className="block px-4 py-2 text-sm text-foreground/70 hover:text-primary">Website Development</Link>
                  <Link href="/services/seo" className="block px-4 py-2 text-sm text-foreground/70 hover:text-primary">SEO</Link>
                  <Link href="/services/google-meta-ads" className="block px-4 py-2 text-sm text-foreground/70 hover:text-primary">Google &amp; Meta Ads</Link>
                </div>
              )}
            </div>

            {/* Mobile Industries Accordion */}
            <div>
              <button
                type="button"
                className="flex items-center justify-between w-full px-4 py-2 text-sm font-medium text-foreground/80 hover:text-primary"
                onClick={() => setMobileIndustriesOpen(!mobileIndustriesOpen)}
                aria-expanded={mobileIndustriesOpen}
                aria-controls="mobile-industries-menu"
              >
                <span>Industries</span>
                <ChevronDown aria-hidden="true" className={`w-4 h-4 transition-transform duration-200 ${mobileIndustriesOpen ? "rotate-180" : ""}`} />
              </button>
              {mobileIndustriesOpen && (
                <div id="mobile-industries-menu" className="pl-4">
                  <Link
                    href="/industries"
                    className="block px-4 py-2 text-sm font-semibold text-primary hover:text-primary/80"
                  >
                    All Industries →
                  </Link>
                  {INDUSTRY_GROUPS.map((group) => (
                    <div key={group.label}>
                      <button
                        type="button"
                        className="flex items-center justify-between w-full px-4 py-2 text-xs font-bold uppercase tracking-wider text-foreground/50 hover:text-foreground/80"
                        onClick={() => toggleMobileCategory(group.label)}
                        aria-expanded={mobileOpenCategories.has(group.label)}
                        aria-controls={`mobile-industry-${group.industries[0].slug}`}
                      >
                        <span>{group.label}</span>
                        <ChevronDown aria-hidden="true" className={`w-3 h-3 transition-transform duration-200 ${mobileOpenCategories.has(group.label) ? "rotate-180" : ""}`} />
                      </button>
                      {mobileOpenCategories.has(group.label) && (
                          <div id={`mobile-industry-${group.industries[0].slug}`} className="pl-4">
                          {group.industries.map((industry) => (
                            <Link
                              key={industry.slug}
                              href={`/industries/${industry.slug}`}
                              className="block px-4 py-1.5 text-sm text-foreground/70 hover:text-primary"
                            >
                              {industry.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Link href="/pricing" className="block px-4 py-2 text-sm font-medium text-foreground/80 hover:text-primary">Pricing</Link>
            <div className="px-4 py-1 text-xs font-bold uppercase tracking-widest text-foreground/40">Platform</div>
            <Link href="/custom-direct-mail" data-testid="nav-custom-direct-mail-mobile" className="block px-6 py-2 text-sm text-foreground/80 hover:text-primary">Custom Direct Mail</Link>
            <Link href="/integrations" className="block px-6 py-2 text-sm text-foreground/80 hover:text-primary">Integrations</Link>
            <Link href="/causal-analytics" className="block px-6 py-2 text-sm text-foreground/80 hover:text-primary">Causal Analytics</Link>
            <Link href="/calculator" className="block px-6 py-2 text-sm text-foreground/80 hover:text-primary">Pricing Calculator</Link>
            <Link href="/map-intel" data-testid="nav-map-intel-mobile" className="block px-6 py-2 text-sm text-foreground/80 hover:text-primary">Map Intel</Link>
            <div className="pt-2 border-t border-border">
              <a
                href={isSignedIn ? "/app/" : "/sign-in?redirect_url=%2Fapp%2F"}
                data-testid="link-app-dashboard-mobile"
                className="block mx-4 mb-2 py-2 bg-primary text-primary-foreground text-sm font-bold rounded-lg text-center"
              >
                Dashboard
              </a>
              <Show when="signed-in">
                <Link href="/orders" className="block px-4 py-2 text-sm font-medium text-foreground/80 hover:text-primary">Orders</Link>
                <Link href="/cart" className="block px-4 py-2 text-sm font-medium text-foreground/80 hover:text-primary">Cart</Link>
              </Show>
              <Show when="signed-out">
                <Link href={`/sign-in?redirect_url=${encodeURIComponent(location + (typeof window !== "undefined" ? window.location.search : ""))}`} className="block px-4 py-2 text-sm font-medium text-foreground/80 hover:text-primary text-center">Login</Link>
                <Link href="/sign-up" onClick={() => { try { localStorage.setItem("gw_signup_source", "header_get_started"); } catch {} }} className="block mx-4 mt-2 py-2 bg-primary text-primary-foreground text-sm font-bold rounded-lg text-center">Get Started</Link>
              </Show>
            </div>
          </nav>
        )}
      </div>

      {/* Desktop Industries Mega Menu — full-width panel below header */}
      {industriesOpen && (
        <div
          id="industries-menu"
          className="hidden lg:block fixed top-16 left-0 right-0 bg-[#1A1A1A] border-b border-border shadow-2xl shadow-black/50 z-40"
          onMouseEnter={() => openMenu(setIndustriesOpen, industriesTimer)}
          onMouseLeave={() => closeMenu(setIndustriesOpen, industriesTimer)}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            {/* "All Industries" link at top */}
            <div className="mb-5 pb-4 border-b border-border/60">
              <Link
                href="/industries"
                className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:text-primary/80 transition-colors"
              >
                All Industries →
              </Link>
            </div>
            {/* Five-column grid of groups */}
            <div className="grid grid-cols-5 gap-6">
              {INDUSTRY_GROUPS.map((group) => (
                <div key={group.label}>
                  <div className="text-xs font-bold uppercase tracking-wider text-foreground/40 mb-3 pb-2 border-b border-border/40">
                    {group.label}
                  </div>
                  <ul className="space-y-1">
                    {group.industries.map((industry) => (
                      <li key={industry.slug}>
                        <Link
                          href={`/industries/${industry.slug}`}
                          className="block text-sm text-foreground/75 hover:text-primary transition-colors py-0.5"
                        >
                          {industry.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
